import { AnalyticsProvider } from '@rhim/analytics';
import config from '@rhim/config';
import { i18nReact } from '@rhim/i18n';
import { ErrorBoundary as RhimErrorBoundary } from '@rhim/react/components/ErrorBoundary';
import { Frame } from '@rhim/react/components/Layout';
import { NoPrint, NoPrintAppWrapper } from '@rhim/react/components/Print';
import { useConnectionListener } from '@rhim/react/hooks/useConnectionListener';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useCallback, useEffect } from 'react';
import { Outlet } from 'react-router';
import styled from 'styled-components';

import { environment } from '../environments/environment';
import { mixpanelAnalytics } from '../lib/analytics';
import { HiddenNavigationProvider } from '../pages/OperatorDisplay/useHiddenNavigation';
import { useLogger } from '../utilities';
import { queryClient } from '../utilities/queryClient';

/** Reusable Inspect providers and UI; the host owns document-level initialization and consent. */
export function InspectProductRuntime({ frameHeight }: Readonly<{ frameHeight?: string }> = {}) {
  return (
    <HiddenNavigationProvider initialValue={false}>
      <QueryClientProvider client={queryClient}>
        <PrintAppFrame>
          <NoPrint>{environment.allowExtensions && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />}</NoPrint>
          <Frame $height={frameHeight}>
            <RhimErrorBoundary>
              <AnalyticsProvider analytics={mixpanelAnalytics}>
                <ProductRuntimeEffects />
                <Outlet />
              </AnalyticsProvider>
            </RhimErrorBoundary>
          </Frame>
        </PrintAppFrame>
      </QueryClientProvider>
    </HiddenNavigationProvider>
  );
}

function ProductRuntimeEffects() {
  useConnectionListener(typeof window !== 'undefined' && window.self === window.top);
  useLogger();

  const handleKeyboardShortcut = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey && config.version === 'dev' && event.key === 'Z') {
      void i18nReact.toggleCimode();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, [handleKeyboardShortcut]);

  return null;
}

const PrintAppFrame = styled(NoPrintAppWrapper)`
  height: 100%;
`;
